import React, { useId, useState } from 'react';
import './NoteViews.css';

export default function NoteViews({ notes = [], label = 'Working notes' }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const readerId = useId();

  if (!notes.length) {
    return (
      <div className="pf-notes-empty" role="status">
        <h3>No notes in this view yet.</h3>
        <p>There is nothing to show here yet.</p>
      </div>
    );
  }

  const activeIndex = Math.min(selectedIndex, notes.length - 1);
  const selectedNote = notes[activeIndex];
  const canMove = notes.length > 1;

  function selectNote(nextIndex) {
    if (nextIndex >= 0 && nextIndex < notes.length) {
      setSelectedIndex(nextIndex);
    }
  }

  function handleKeyDown(event) {
    if (!canMove || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectNote(activeIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectNote(activeIndex - 1);
    }
  }

  return (
    <div className="pf-note-reader pf-note-reader-simple">
      <section
        className="pf-note-carousel"
        aria-roledescription="carousel"
        aria-label={label}
        aria-describedby={readerId + '-keys'}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className="pf-carousel-topline">
          <p className="pf-note-stage">{selectedNote.stage || 'Working note'}</p>
          <span className="pf-carousel-count">
            {String(activeIndex + 1).padStart(2, '0')} <span>/ {String(notes.length).padStart(2, '0')}</span>
          </span>
        </div>
        <article
          className="pf-carousel-slide"
          key={selectedNote.slug}
          role="group"
          aria-roledescription="slide"
          aria-label={String(activeIndex + 1) + ' of ' + String(notes.length)}
        >
          <h3>{selectedNote.title}</h3>
          <div className="pf-carousel-body">
            {(selectedNote.body?.length ? selectedNote.body : [selectedNote.summary]).map((paragraph, index) => (
              <p key={selectedNote.slug + '-' + index}>{paragraph}</p>
            ))}
          </div>
        </article>
        {canMove && (
          <div className="pf-carousel-controls">
            <button type="button" onClick={() => selectNote(activeIndex - 1)} disabled={activeIndex === 0}>
              <span aria-hidden="true">←</span> Previous
            </button>
            <p id={readerId + '-keys'} className="pf-carousel-hint">Use ← and → to move through the notes.</p>
            <button type="button" onClick={() => selectNote(activeIndex + 1)} disabled={activeIndex === notes.length - 1}>
              Next <span aria-hidden="true">→</span>
            </button>
          </div>
        )}
        {!canMove && <p id={readerId + '-keys'} className="pf-visually-hidden">One note in this view.</p>}
        <p className="pf-visually-hidden" aria-live="polite" aria-atomic="true">
          Note {activeIndex + 1} of {notes.length}: {selectedNote.title}
        </p>
      </section>
    </div>
  );
}
